import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  applyDateMaskEdit,
  backspaceDateMask,
  formatDateMask,
  INVALID_DATE_MESSAGE,
  isInvalidCompleteDate,
  isoToUsDateMask,
  parseUsDateToIso,
} from '@/lib/date-mask';
import { Error } from './Error';

type DateTimeRowProps = {
  date?: number | null;
  startTime?: number | null;
  endTime?: number | null;
  selectedDate?: string | null;
  dateInput?: string | null;
  onRowAdd: () => void;
  onRowDelete: (index: number) => void;
  index: number;
  allowMultiple?: boolean;
  onDateChange: (index: number, ms: number | null) => void;
  onDateInputChange?: (
    index: number,
    dateInput: string | null,
    isoDate: string | null,
  ) => void;
  onStartTimeChange: (index: number, ms: number | null) => void;
  onEndTimeChange: (index: number, ms: number | null) => void;
  readOnly?: boolean;
};

function formatTimeInput(ms: number | null | undefined) {
  if (ms == null) {
    return '';
  }
  return moment(ms).format('HH:mm');
}

function applyTimeOfDay(dateStr: string, fromMs: number) {
  const prev = moment(fromMs);
  return moment(dateStr, 'YYYY-MM-DD')
    .hour(prev.hour())
    .minute(prev.minute())
    .second(0)
    .millisecond(0)
    .valueOf();
}

function draftFromProps({
  dateInput,
  selectedDate,
  startTimeMS,
}: {
  dateInput?: string | null;
  selectedDate?: string | null;
  startTimeMS?: number | null;
}) {
  if (dateInput) {
    return formatDateMask(dateInput);
  }
  if (selectedDate) {
    return isoToUsDateMask(selectedDate);
  }
  if (startTimeMS != null) {
    return isoToUsDateMask(startTimeMS);
  }
  return '';
}

export function DateTimeRow({
  date: startTimeMS,
  startTime: _startDup,
  endTime: endTimeMS,
  selectedDate = null,
  dateInput = null,
  onRowAdd,
  onRowDelete,
  index,
  allowMultiple,
  onDateChange,
  onDateInputChange,
  onStartTimeChange,
  onEndTimeChange,
  readOnly = false,
}: DateTimeRowProps) {
  const idPrefix = useId();
  const dateFieldId = `${idPrefix}-date`;
  const startFieldId = `${idPrefix}-start`;
  const endFieldId = `${idPrefix}-end`;
  const dateHintId = `${idPrefix}-date-hint`;
  const dateErrorId = `${idPrefix}-date-error`;

  // Local draft is the input source of truth so invalid intermediate values can be typed.
  const [draft, setDraft] = useState(() =>
    draftFromProps({ dateInput, selectedDate, startTimeMS }),
  );
  const isEditingRef = useRef(false);
  // Keep prior start time-of-day when the date field is cleared then retyped.
  const preservedStartMsRef = useRef<number | null>(null);
  const nativePickerRef = useRef<HTMLInputElement>(null);

  const dateInputRef = useRef<HTMLInputElement>(null);
  const pendingCaretRef = useRef<number | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const timesColRef = useRef<HTMLDivElement>(null);
  const [timesWrapped, setTimesWrapped] = useState(false);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const timesCol = timesColRef.current;
    if (!row || !timesCol) {
      return;
    }

    const updateWrapped = () => {
      const styles = getComputedStyle(row);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      const rootFontSize = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const datePreferredPx = 10.5 * rootFontSize;
      const timesWidth = timesCol.scrollWidth;
      const fits = datePreferredPx + gap + timesWidth <= row.clientWidth + 0.5;
      setTimesWrapped(!fits);
    };

    updateWrapped();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWrapped);
      return () => window.removeEventListener('resize', updateWrapped);
    }

    const observer = new ResizeObserver(updateWrapped);
    observer.observe(row);
    observer.observe(timesCol);
    return () => observer.disconnect();
  }, [allowMultiple, readOnly]);

  useEffect(() => {
    if (isEditingRef.current) {
      return;
    }
    setDraft(draftFromProps({ dateInput, selectedDate, startTimeMS }));
  }, [dateInput, startTimeMS, selectedDate]);

  useEffect(() => {
    const caret = pendingCaretRef.current;
    const el = dateInputRef.current;
    if (caret == null || !el) {
      return;
    }
    pendingCaretRef.current = null;
    el.setSelectionRange(caret, caret);
  }, [draft]);

  const startTimeStr = formatTimeInput(startTimeMS);
  const endTimeStr = formatTimeInput(endTimeMS);
  const showInvalidDate = isInvalidCompleteDate(draft);
  const nativePickerValue =
    parseUsDateToIso(draft) ||
    selectedDate ||
    (startTimeMS != null ? moment(startTimeMS).format('YYYY-MM-DD') : '') ||
    '';

  const resolveDayIso = () =>
    parseUsDateToIso(draft) ||
    selectedDate ||
    (startTimeMS != null ? moment(startTimeMS).format('YYYY-MM-DD') : null) ||
    moment().format('YYYY-MM-DD');

  const commitMaskedDate = (masked: string, caret?: number) => {
    isEditingRef.current = true;
    if (caret != null) {
      pendingCaretRef.current = caret;
    }
    setDraft(masked);
    const iso = parseUsDateToIso(masked);

    if (!masked) {
      if (startTimeMS != null) {
        preservedStartMsRef.current = startTimeMS;
      }
      onDateInputChange?.(index, null, null);
      onDateChange(index, null);
      return;
    }

    onDateInputChange?.(index, masked, iso);

    const timeBase = startTimeMS ?? preservedStartMsRef.current;
    if (iso && timeBase != null) {
      onDateChange(index, applyTimeOfDay(iso, timeBase));
      preservedStartMsRef.current = null;
    }
  };

  const openNativePicker = () => {
    const el = nativePickerRef.current;
    if (!el || readOnly) {
      return;
    }
    try {
      if (typeof el.showPicker === 'function') {
        el.showPicker();
        return;
      }
    } catch {
      // showPicker can throw if not triggered by a user gesture in some browsers.
    }
    el.click();
  };

  const dateDescribedBy = showInvalidDate
    ? `${dateHintId} ${dateErrorId}`
    : dateHintId;

  const fieldLabelClass =
    'm-0 mt-1 block italic leading-normal text-[0.8rem] text-[var(--muted-foreground)]';

  return (
    <div
      ref={rowRef}
      className={`festy-datetime-row flex flex-wrap items-start gap-3${timesWrapped ? ' festy-datetime-row--stacked' : ''}`}
    >
      <div className="festy-datetime-date">
        <div
          className={`festy-datetime-date-field${showInvalidDate ? ' festy-datetime-date-field--invalid' : ''}`}
        >
          <input
            ref={dateInputRef}
            id={dateFieldId}
            type="text"
            inputMode="numeric"
            placeholder="MM/DD/YYYY"
            autoComplete="off"
            className="festy-datetime-input festy-datetime-input--with-calendar"
            value={draft}
            disabled={readOnly}
            aria-invalid={showInvalidDate}
            aria-describedby={dateDescribedBy}
            onFocus={() => {
              isEditingRef.current = true;
            }}
            onBlur={() => {
              // Keep the local draft. Resetting from props here races the parent
              // update from the last keystroke and clears freshly typed dates.
              isEditingRef.current = false;
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Backspace' || e.nativeEvent.isComposing) {
                return;
              }
              const el = e.currentTarget;
              const caret = el.selectionStart ?? 0;
              const end = el.selectionEnd ?? caret;
              if (caret !== end) {
                return;
              }
              const handled = backspaceDateMask(draft, caret);
              if (!handled) {
                return;
              }
              e.preventDefault();
              commitMaskedDate(handled.masked, handled.caret);
            }}
            onChange={(e) => {
              const el = e.target;
              const result = applyDateMaskEdit({
                previous: draft,
                nextRaw: el.value,
                nextCaret: el.selectionStart ?? el.value.length,
              });
              commitMaskedDate(result.masked, result.caret);
            }}
          />
          <button
            type="button"
            className="festy-datetime-calendar-btn"
            aria-label="Open calendar"
            disabled={readOnly}
            onClick={(e) => {
              e.preventDefault();
              openNativePicker();
            }}
          >
            <FontAwesomeIcon icon={['far', 'calendar-days']} aria-hidden />
          </button>
          <input
            ref={nativePickerRef}
            type="date"
            className="festy-datetime-native-picker"
            tabIndex={-1}
            aria-hidden="true"
            disabled={readOnly}
            value={nativePickerValue}
            onChange={(e) => {
              const iso = e.target.value;
              if (!iso) {
                commitMaskedDate('');
                return;
              }
              commitMaskedDate(isoToUsDateMask(iso));
            }}
          />
        </div>
        <label htmlFor={dateFieldId} className={fieldLabelClass}>
          Date
        </label>
        <span id={dateHintId} className="sr-only">
          Format MM/DD/YYYY. You can type the date or use the Open calendar
          button.
        </span>
        {showInvalidDate ? (
          <Error id={dateErrorId} role="alert">
            {INVALID_DATE_MESSAGE}
          </Error>
        ) : null}
      </div>
      <div
        ref={timesColRef}
        className="festy-datetime-times flex flex-nowrap items-start gap-3"
      >
        <div>
          <input
            id={startFieldId}
            type="time"
            className="festy-datetime-input"
            value={startTimeStr}
            disabled={readOnly}
            onChange={(e) => {
              const t = e.target.value;
              if (!t) {
                if (startTimeMS != null) {
                  const dayMask = isoToUsDateMask(startTimeMS);
                  const dayIso = moment(startTimeMS).format('YYYY-MM-DD');
                  setDraft(dayMask);
                  onDateInputChange?.(index, dayMask, dayIso);
                }
                onStartTimeChange(index, null);
                return;
              }
              const dayIso = resolveDayIso();
              const ms = moment(`${dayIso}T${t}`, 'YYYY-MM-DDTHH:mm').valueOf();
              onStartTimeChange(index, ms);
            }}
          />
          <label htmlFor={startFieldId} className={fieldLabelClass}>
            Start Time
          </label>
        </div>
        <div>
          <input
            id={endFieldId}
            type="time"
            className="festy-datetime-input"
            value={endTimeStr}
            disabled={readOnly}
            onChange={(e) => {
              const t = e.target.value;
              if (!t) {
                onEndTimeChange(index, null);
                return;
              }
              const dayIso = resolveDayIso();
              const ms = moment(`${dayIso}T${t}`, 'YYYY-MM-DDTHH:mm').valueOf();
              onEndTimeChange(index, ms);
            }}
          />
          <label htmlFor={endFieldId} className={fieldLabelClass}>
            End Time
          </label>
        </div>
        {allowMultiple && !readOnly ? (
          <div className="festy-datetime-actions flex flex-nowrap items-center gap-2 pt-[0.65rem]">
            <button
              type="button"
              className="festy-datetime-action-btn"
              aria-label="Add Time"
              title="Add Time"
              onClick={(e) => {
                e.preventDefault();
                onRowAdd();
              }}
            >
              <FontAwesomeIcon icon={['fas', 'circle-plus']} aria-hidden />
            </button>
            <button
              type="button"
              className="festy-datetime-action-btn"
              aria-label="Delete Time"
              title="Delete Time"
              onClick={(e) => {
                e.preventDefault();
                onRowDelete(index);
              }}
            >
              <FontAwesomeIcon icon={['far', 'trash-can']} aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
